import React, { useState, useEffect, useCallback } from 'react';
import { signInAnonymously } from 'firebase/auth';
import {
  collection, getDocs, doc, updateDoc, writeBatch, serverTimestamp, FieldValue,
} from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import './AmbassadorApplicationsPage.css';

interface AmbassadorApplication {
  id: string;
  fullName?: string;
  email?: string;
  cityCountry?: string;
  instagram?: string;
  tiktok?: string;
  followerCount?: string;
  raceName?: string;
  raceDate?: string;
  upcomingRaces?: string; // Legacy free-text field, before raceName/raceDate
  whyRally?: string;
  status?: string;
  emailSent?: boolean;
  approvedAt?: any;
  rejectedAt?: any;
  emailedAt?: any;
  cleared?: boolean; // Hidden from the rejected tab; the document is kept
  clearedAt?: any;
  timestamp: any;
}

type Tab = 'new' | 'approved' | 'rejected';

const PASSWORD = 'niamh';
const STORAGE_KEY = 'ambassador_applications_unlocked';

const TABS: { id: Tab; label: string }[] = [
  { id: 'new', label: 'New' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

const EMPTY_COPY: Record<Tab, string> = {
  new: 'No new applications.',
  approved: 'Nothing waiting to be emailed.',
  rejected: 'No rejected applications to clear.',
};

const formatDateTime = (timestamp: any): string => {
  if (!timestamp) return '—';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

// raceDate is a plain YYYY-MM-DD string — parse the parts by hand so it isn't
// read as UTC midnight and shifted back a day.
const formatRaceDate = (value?: string): string => {
  if (!value) return '—';
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const getTime = (a: AmbassadorApplication): number =>
  a.timestamp ? (a.timestamp.toDate ? a.timestamp.toDate().getTime() : new Date(a.timestamp).getTime()) : 0;

const ensureAuth = async () => {
  if (!auth.currentUser) await signInAnonymously(auth);
};

// serverTimestamp() writes a sentinel, not a value, so the optimistic local
// update carries only the plain fields.
const withoutSentinels = (fields: Record<string, any>): Record<string, any> => {
  const plain: Record<string, any> = {};
  Object.keys(fields).forEach(key => {
    if (!(fields[key] instanceof FieldValue)) plain[key] = fields[key];
  });
  return plain;
};

// ── Table ────────────────────────────────────────────────────────────────────

interface ApplicationTableProps {
  rows: AmbassadorApplication[];
  tab: Tab;
  selected: Set<string>;
  processing: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onReopen: (id: string) => void;
  onMarkEmailed: (id: string) => void;
  onBulkApprove: () => void;
  onBulkEmailed: () => void;
  onBulkClear: () => void;
}

const ApplicationTable: React.FC<ApplicationTableProps> = ({
  rows, tab, selected, processing,
  onToggle, onToggleAll, onApprove, onReject, onReopen, onMarkEmailed,
  onBulkApprove, onBulkEmailed, onBulkClear,
}) => {
  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));

  const BULK: Record<Tab, { label: string; run: () => void }> = {
    new: { label: 'Approve', run: onBulkApprove },
    approved: { label: 'Mark Emailed', run: onBulkEmailed },
    rejected: { label: 'Clear', run: onBulkClear },
  };
  const bulk = BULK[tab];

  return (
    <>
      {selected.size > 0 && (
        <div className="aa-bulk-bar">
          <span>{selected.size} selected</span>
          <button className="aa-bulk-btn" onClick={bulk.run}>
            {bulk.label} ({selected.size})
          </button>
        </div>
      )}
      <div className="aa-table-wrapper">
        <table className="aa-table">
          <thead>
            <tr>
              <th className="aa-th-check">
                <input type="checkbox" checked={allSelected} onChange={onToggleAll} />
              </th>
              <th>Applied</th>
              <th>Name</th>
              <th>Email</th>
              <th>Location</th>
              <th>Instagram</th>
              <th>TikTok</th>
              <th>Followers</th>
              <th>Race</th>
              <th>Race date</th>
              <th>Why Rally</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className={selected.has(r.id) ? 'aa-row-selected' : ''}>
                <td className="aa-td-check">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => onToggle(r.id)}
                  />
                </td>
                <td className="aa-cell-date">{formatDateTime(r.timestamp)}</td>
                <td className="aa-cell-name">{r.fullName ?? '—'}</td>
                <td className="aa-cell-muted">
                  {r.email ? <a href={`mailto:${r.email}`}>{r.email}</a> : '—'}
                </td>
                <td>{r.cityCountry ?? '—'}</td>
                <td className="aa-cell-social">
                  {r.instagram ? (
                    <a
                      href={`https://instagram.com/${r.instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      @{r.instagram}
                    </a>
                  ) : '—'}
                </td>
                <td className="aa-cell-social">
                  {r.tiktok ? (
                    <a
                      href={`https://tiktok.com/@${r.tiktok}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      @{r.tiktok}
                    </a>
                  ) : '—'}
                </td>
                <td>{r.followerCount ?? '—'}</td>
                <td className="aa-cell-race">{r.raceName || r.upcomingRaces || '—'}</td>
                <td className="aa-cell-racedate">{formatRaceDate(r.raceDate)}</td>
                <td className="aa-cell-why">{r.whyRally || '—'}</td>
                <td className="aa-cell-action">
                  {tab === 'new' && (
                    <>
                      <button
                        className="aa-btn aa-btn--approve"
                        onClick={() => onApprove(r.id)}
                        disabled={processing.has(r.id)}
                      >
                        {processing.has(r.id) ? <span className="aa-btn-spinner" /> : 'Approve'}
                      </button>
                      <button
                        className="aa-btn aa-btn--reject"
                        onClick={() => onReject(r.id)}
                        disabled={processing.has(r.id)}
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {tab === 'approved' && (
                    <button
                      className="aa-btn aa-btn--approve"
                      onClick={() => onMarkEmailed(r.id)}
                      disabled={processing.has(r.id)}
                    >
                      {processing.has(r.id) ? <span className="aa-btn-spinner" /> : 'Mark Emailed'}
                    </button>
                  )}
                  {tab === 'rejected' && (
                    <button
                      className="aa-btn aa-btn--approve"
                      onClick={() => onReopen(r.id)}
                      disabled={processing.has(r.id)}
                    >
                      {processing.has(r.id) ? <span className="aa-btn-spinner" /> : 'Undo'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────

const AmbassadorApplicationsPage: React.FC = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('new');

  const [applications, setApplications] = useState<AmbassadorApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [processing, setProcessing] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === 'true') setIsUnlocked(true);
  }, []);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      await ensureAuth();
      const snapshot = await getDocs(collection(db, 'ambassadorApplications'));
      const data = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() } as AmbassadorApplication))
        .sort((a, b) => getTime(b) - getTime(a)); // newest first
      setApplications(data);
    } catch (err) {
      console.error('Error fetching ambassador applications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isUnlocked) fetchApplications();
  }, [isUnlocked, fetchApplications]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput !== PASSWORD) {
      setPasswordError('Incorrect password');
      setPasswordInput('');
      return;
    }
    try {
      await signInAnonymously(auth);
      localStorage.setItem(STORAGE_KEY, 'true');
      setIsUnlocked(true);
      setPasswordError('');
    } catch (err) {
      console.error('Auth error:', err);
      setPasswordError('Authentication failed. Please try again.');
    }
  };

  // ── Status changes ─────────────────────────────────────────────────────────

  // Rows leave the view once they're handled, so every decision is stamped in
  // Firestore — that's the record from here on.
  const APPROVE = { status: 'approved', approvedAt: serverTimestamp() };
  const REJECT = { status: 'rejected', rejectedAt: serverTimestamp() };
  const REOPEN = { status: 'new' };
  const EMAILED = { emailSent: true, emailedAt: serverTimestamp() };
  const CLEAR = { cleared: true, clearedAt: serverTimestamp() };

  const updateOne = async (id: string, fields: Record<string, any>) => {
    setProcessing(prev => new Set(prev).add(id));
    try {
      await updateDoc(doc(db, 'ambassadorApplications', id), fields);
      const local = withoutSentinels(fields);
      setApplications(prev => prev.map(a => (a.id === id ? { ...a, ...local } : a)));
      setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
    } catch (err) {
      console.error('Error updating application:', err);
    } finally {
      setProcessing(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const bulkUpdate = async (fields: Record<string, any>) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setProcessing(new Set(ids));
    try {
      const batch = writeBatch(db);
      ids.forEach(id => batch.update(doc(db, 'ambassadorApplications', id), fields));
      await batch.commit();
      const local = withoutSentinels(fields);
      setApplications(prev =>
        prev.map(a => (ids.includes(a.id) ? { ...a, ...local } : a))
      );
      setSelected(new Set());
    } catch (err) {
      console.error('Error in bulk update:', err);
    } finally {
      setProcessing(new Set());
    }
  };

  // Clearing only hides the rows — deletes are blocked so the audit trail
  // stays intact — but there's no way back in the UI, so confirm first.
  const bulkClear = () => {
    const count = selected.size;
    if (!count) return;
    const noun = count === 1 ? 'application' : 'applications';
    if (!window.confirm(`Clear ${count} rejected ${noun} from this view? They stay in the database.`)) return;
    bulkUpdate(CLEAR);
  };

  // ── Derived rows ───────────────────────────────────────────────────────────

  const rowsFor = (tab: Tab): AmbassadorApplication[] =>
    applications.filter(a => {
      const status = a.status ?? 'new';
      if (tab === 'new') return status === 'new';
      // Emailed and cleared rows are done with — keep the tabs to what's left
      // to action.
      if (tab === 'approved') return status === 'approved' && a.emailSent !== true;
      return status === 'rejected' && a.cleared !== true;
    });

  const rows = rowsFor(activeTab);

  const toggle = useCallback((id: string) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected(prev => (prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id))));
  }, [rows]);

  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    setSelected(new Set());
  };

  // ── Password screen ────────────────────────────────────────────────────────

  if (!isUnlocked) {
    return (
      <div className="aa-lock-screen">
        <div className="aa-lock-card">
          <h2 className="aa-lock-title">Ambassador Applications</h2>
          <form onSubmit={handlePasswordSubmit}>
            <input
              className="aa-lock-input"
              type="password"
              value={passwordInput}
              onChange={e => setPasswordInput(e.target.value)}
              placeholder="Password"
              autoFocus
            />
            {passwordError && <p className="aa-lock-error">{passwordError}</p>}
            <button className="aa-lock-btn" type="submit">Unlock</button>
          </form>
        </div>
      </div>
    );
  }

  // ── Main UI ────────────────────────────────────────────────────────────────

  return (
    <div className="aa-page">
      <div className="aa-hero">
        <h1 className="aa-hero-rally">Rally</h1>
        <p className="aa-hero-tagline">Ambassador Applications</p>
      </div>

      <div className="aa-tabs">
        {TABS.map(tab => {
          const count = rowsFor(tab.id).length;
          return (
            <button
              key={tab.id}
              className={`aa-tab${activeTab === tab.id ? ' aa-tab--active' : ''}`}
              onClick={() => switchTab(tab.id)}
            >
              {tab.label}
              {count > 0 && <span className="aa-tab-badge">{count}</span>}
            </button>
          );
        })}
      </div>

      {loading && <div className="aa-loading"><div className="aa-spinner" /></div>}
      {!loading && rows.length === 0 && (
        <div className="aa-empty">{EMPTY_COPY[activeTab]}</div>
      )}
      {!loading && rows.length > 0 && (
        <ApplicationTable
          rows={rows}
          tab={activeTab}
          selected={selected}
          processing={processing}
          onToggle={toggle}
          onToggleAll={toggleAll}
          onApprove={id => updateOne(id, APPROVE)}
          onReject={id => updateOne(id, REJECT)}
          onReopen={id => updateOne(id, REOPEN)}
          onMarkEmailed={id => updateOne(id, EMAILED)}
          onBulkApprove={() => bulkUpdate(APPROVE)}
          onBulkEmailed={() => bulkUpdate(EMAILED)}
          onBulkClear={bulkClear}
        />
      )}
    </div>
  );
};

export default AmbassadorApplicationsPage;
